import { Controller } from 'stimulus'
import { loadStripe, Stripe, StripeElements } from '@stripe/stripe-js'
import { getLogger } from '@blueberry/observability'

const logger = getLogger('balance/balance-payment_controller')

// Balance paid-signup Google Ads conversion. Fired once on payment completion.
const SUCCESS_CONVERSION = 'AW-816975002/TCD3CLKct8QcEJqZyIUD'

// Balance Meta Pixel funnel event, fired once on payment completion. Its name mirrors
// meta_events.SUCCESS server-side (balance/balance_signup/meta_events.py). Fired here
// rather than via a rendered page because completion returns an AJAX JSON redirect.
const SUCCESS_META_EVENT = 'balance_success'

// Max time to wait for the conversion hit before redirecting anyway.
const CONVERSION_TIMEOUT_MS = 1000

declare global {
  interface Window {
    gtag: (command: string, event: string, payload?: unknown) => void
    fbq: (
      command: 'track' | 'init' | 'trackCustom',
      event: string,
      data?: unknown,
      options?: { eventID: string },
    ) => void
  }
}

export default class extends Controller {
  static targets = ['submitButton', 'error', 'newCard']
  declare submitButtonTarget: HTMLButtonElement
  declare errorTargets: HTMLElement[]
  declare hasNewCardTarget: boolean
  declare newCardTarget: HTMLElement

  static values = {
    stripePublishableKey: String,
    confirmUrl: String,
    billingName: String,
    billingEmail: String,
    hasStoredCard: Boolean,
  }
  declare stripePublishableKeyValue: string
  declare confirmUrlValue: string
  declare billingNameValue: string
  declare billingEmailValue: string
  declare hasStoredCardValue: boolean

  private stripe: Stripe | null = null
  private elements: StripeElements | null = null

  async connect() {
    if (!this.stripePublishableKeyValue) {
      logger.error('Stripe publishable key is missing')
      return
    }

    try {
      await this.initializeStripe()
    } catch (error) {
      logger.error('Error initializing Stripe:', error)
    }
  }

  async initializeStripe() {
    const stripe = await loadStripe(this.stripePublishableKeyValue)
    this.stripe = stripe

    const elements = stripe?.elements({
      mode: 'setup',
      currency: 'usd',
      paymentMethodCreation: 'manual',
      appearance: {
        variables: {
          colorPrimary: '#235aff',
          colorBackground: '#e0e7ff',
          colorText: '#111827',
          colorDanger: '#ef4444',
          fontFamily: 'Inter, system-ui, sans-serif',
          borderRadius: '8px',
        },
      },
    })

    if (!elements) {
      logger.error('Stripe Elements not available')
      return
    }
    this.elements = elements

    const paymentElement = elements.create('payment', {
      layout: 'tabs',
      fields: {
        billingDetails: {
          name: 'never',
          email: 'never',
        },
      },
    })

    paymentElement.mount('#payment-element')

    // Billing address + phone. The phone gets attached to the PaymentMethod's
    // billing_details, so we read it back server-side instead of collecting it
    // in our own field.
    const addressElement = elements.create('address', {
      mode: 'billing',
      fields: { phone: 'always' },
      validation: { phone: { required: 'always' } },
      defaultValues: { name: this.billingNameValue },
    })

    addressElement.mount('#address-element')
  }

  handleSubmit(event: Event) {
    event.preventDefault()
    void this.submitPaidSignup()
  }

  // Logged-in flow: toggle the Stripe card fields based on the stored-card vs
  // add-new-card radio.
  selectPaymentOption() {
    if (this.hasNewCardTarget) {
      this.newCardTarget.hidden = this.usingStoredCard()
    }
  }

  private usingStoredCard(): boolean {
    if (!this.hasStoredCardValue) return false
    const selected = this.element.querySelector<HTMLInputElement>(
      'input[name="payment_option"]:checked',
    )
    return selected?.value !== 'new'
  }

  private async submitPaidSignup() {
    // Logged-in flow reusing the card on file — no Stripe Element collection needed.
    if (this.usingStoredCard()) {
      void this.confirmWith({ use_stored_card: true })
      return
    }

    if (!this.stripe || !this.elements) {
      logger.error('Stripe not initialized')
      this.showError()
      return
    }

    this.hideError()
    this.setLoading()

    try {
      const { error: submitError } = await this.elements.submit()
      if (submitError) {
        logger.error(submitError.message)
        this.showError()
        this.setNotLoading()
        return
      }

      const { error: pmError, paymentMethod } =
        await this.stripe.createPaymentMethod({
          elements: this.elements,
          params: {
            billing_details: {
              name: this.billingNameValue,
              email: this.billingEmailValue,
            },
          },
        })

      if (pmError || !paymentMethod) {
        logger.error(pmError?.message ?? 'Missing payment method')
        this.showError()
        this.setNotLoading()
        return
      }

      await this.confirmWith({ payment_method_id: paymentMethod.id })
    } catch (error) {
      logger.error('Error submitting balance paid signup', error)
      this.showError()
      this.setNotLoading()
    }
  }

  // POST the payment selection to the server and redirect on success. Shared by the
  // new-card path (payment_method_id) and the stored-card path (use_stored_card).
  private async confirmWith(payload: Record<string, unknown>) {
    this.hideError()
    this.setLoading()
    try {
      const response = await fetch(this.confirmUrlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': this.csrfToken(),
        },
        body: JSON.stringify(payload),
      })

      const body = (await response.json()) as {
        success?: boolean
        redirect_to?: string
        meta_event_id?: string
      }

      if (!response.ok || !body.success || !body.redirect_to) {
        logger.error('Balance paid signup confirm failed')
        this.showError()
        this.setNotLoading()
        return
      }

      this.trackMetaSuccess(body.meta_event_id)
      await this.trackConversion()
      window.location.href = body.redirect_to
    } catch (error) {
      logger.error('Error submitting balance paid signup confirm', error)
      this.showError()
      this.setNotLoading()
    }
  }

  private csrfToken(): string {
    const input = this.element.querySelector<HTMLInputElement>(
      'input[name="csrfmiddlewaretoken"]',
    )
    return input?.value ?? ''
  }

  private setLoading() {
    this.submitButtonTarget.disabled = true
    this.submitButtonTarget.innerHTML =
      '<span class="loading loading-spinner loading-sm"></span>'
  }

  private setNotLoading() {
    this.submitButtonTarget.disabled = false
    this.submitButtonTarget.innerHTML = 'Sign up'
  }

  private showError() {
    this.errorTargets.forEach((target) => (target.hidden = false))
  }

  private hideError() {
    this.errorTargets.forEach((target) => (target.hidden = true))
  }

  // Fire the Balance Meta Pixel success event. Best-effort and non-blocking: the
  // trackConversion() wait that follows gives the pixel request time to leave before
  // the redirect to /balance/welcome. fbq is defined by the base loader in analytics.html.
  // eventID (minted server-side, echoed back in the confirm response) is shared with the
  // CAPI event so Meta de-dupes the browser + server pair.
  private trackMetaSuccess(eventID?: string) {
    try {
      const options = eventID ? { eventID } : undefined
      window.fbq?.('trackCustom', SUCCESS_META_EVENT, {}, options)
    } catch (error) {
      logger.error('Error tracking Meta Pixel success event:', error)
    }
  }

  // Wait for the conversion hit to be acknowledged before navigating, so the
  // redirect to /balance/welcome doesn't cancel it in flight. event_callback fires
  // once gtag has sent the hit; the timeout is a fallback so a blocked or slow
  // tag (e.g. an ad blocker) never strands the user on the payment page.
  private trackConversion(): Promise<void> {
    return new Promise<void>((resolve) => {
      let settled = false
      const done = () => {
        if (settled) return
        settled = true
        resolve()
      }
      try {
        const timeout = window.setTimeout(done, CONVERSION_TIMEOUT_MS)
        window.gtag('event', 'conversion', {
          send_to: SUCCESS_CONVERSION,
          event_callback: () => {
            window.clearTimeout(timeout)
            done()
          },
        })
      } catch (error) {
        logger.error('Error tracking Google conversion:', error)
        done()
      }
    })
  }
}
